import { Circle } from 'src/api/circles';
import api, { apiNoAuth } from './baseApi';

export enum AuctionStatus {
  CREATING = 'CREATING', // WebAPP only state
  PENDING = 'PENDING', // auction has been created but not yet open
  OPEN = 'OPEN', // accepting bids
  CLOSING = 'CLOSING', // no longer accepting bids, allow bids in progress to complete
  // TODO CLOSED should be ENDED as it has not CLOSED yet
  CLOSED = 'CLOSED', // no longer accepting bids, no more bids allowed
  CANCELED = 'CANCELED', // auction was canceled before it was closed
  ERROR = 'ERROR', // General error or unexpected failure during bidding
}

export interface AuctionWithTribe {
  _id: string;
  createdAt: string;
  updatedAt: string;

  status: AuctionStatus;
  tribe_id: string;
  tribe: Circle;
  open_seat_count: number;
  valid_bid_amounts: typeof BID_AMOUNTS;
  openDate: string;
  closeDate: string;

  bidDetails: AuctionBidDetails;
}

export interface AuctionBidDetails {
  totalBids: number;
  highestBid: number;
  lowestBid: number;
  lowestAvailableBid: number;
  lowestWinningBid: number;
}

export interface PublicAuctionWithTribe {
  _id: string;
  status: AuctionStatus;
  openDate: string;
  closeDate: string;
  open_seat_count: number;
  valid_bid_amounts: typeof BID_AMOUNTS;
  tribe: Circle;
  bidDetails: {
    lowestAvailableBid: number;
  };
}

export interface Bid {
  _id: string;
  createdAt: string;
  updatedAt: string;
  auction_id: string;
  tribe_id: string;
  user_id: string;
  bid_amount_usd: ValidBidAmount;
  bid_status: BidStatus;
  payment_status: BidPaymentStatus;
  is_winning_bid: boolean;
  stripeSession?: StripeCheckoutSession;
}

export interface StripeCheckoutSession {
  amount: number;
  redirectUrl: string;
}

export enum BidStatus {
  PENDING = 'PENDING', // Bid created
  PROCESSING = 'PROCESSING', // Bid being processed by Stripe
  CANCELLED = 'CANCELLED', // User cancels their bid before auction closes
  SUCCESSFUL = 'SUCCESSFUL', // Auction closed, bid was high enough to secure a seat
  UNSUCCESSFUL = 'UNSUCCESSFUL', // Auction closed or bid outbid by a higher offer
  ERROR = 'ERROR', // General error or unexpected failure during bidding
}

export enum BidPaymentStatus {
  PENDING = 'PENDING',
  VERIFIED = 'VERIFIED',
  PAYMENT_HOLD = 'PAYMENT_HOLD',
  PAYMENT_HOLD_FAILED = 'PAYMENT_HOLD_FAILED',
  PAYMENT_HOLD_CANCELLED = 'PAYMENT_HOLD_CANCELLED',
  PAID = 'PAID',
  FAILED = 'FAILED',
}

// use serverless returned amounts
export const BID_AMOUNTS = [
  1000, 2500, 5000, 10000, 25000, 50000, 100000, 250000, 500000,
] as const;
export type ValidBidAmount = (typeof BID_AMOUNTS)[number];

export const auctionApi = {
  createAuction: async (
    circleId: string,
    auctionData: any
  ): Promise<AuctionWithTribe> => {
    const axiosResponse = await api.post(
      `tribes/${circleId}/auctions`,
      auctionData
    );
    return axiosResponse.data.result;
  },

  cancelAuction: async (auctionId: string) => {
    const axiosResponse = await api.delete(`auctions/${auctionId}`);
    return axiosResponse.data.result;
  },

  getAuctionList: async (tribeId: string): Promise<AuctionWithTribe[]> => {
    const axiosResponse = await api.get(`tribes/${tribeId}/auctions`);
    return axiosResponse.data.result;
  },

  getPublicAuctionData: async (
    auctionId: string
  ): Promise<PublicAuctionWithTribe> => {
    // TODO add dto mappers for underscores and renames
    const axiosResponse = await apiNoAuth.get(`auctions/${auctionId}/public`);
    return axiosResponse.data.result;
  },

  getSpecificAuction: async (auctionId: string): Promise<AuctionWithTribe> => {
    const axiosResponse = await api.get(`auctions/${auctionId}`);
    return axiosResponse.data.result;
  },

  getUserBids: async (auctionId: string): Promise<Bid[]> => {
    const axiosResponse = await api.get(`bids/?auction_id=${auctionId}`);
    return axiosResponse.data.result;
  },

  createBid: async (
    auctionId: string,
    bid_amount_usd: number
  ): Promise<Bid> => {
    const axiosResponse = await api.post(`auctions/${auctionId}/bids`, {
      bid_amount_usd,
    });
    return axiosResponse.data.result;
  },

  updateBidAmount: async (
    bid_id: string,
    bid_amount_usd: number
  ): Promise<Bid> => {
    const axiosResponse = await api.patch(`bids/${bid_id}`, { bid_amount_usd });
    return axiosResponse.data.result;
  },

  updateBidStatus: async (
    bid_id: string,
    bid_status: BidStatus
  ): Promise<Bid> => {
    const axiosResponse = await api.patch(`bids/${bid_id}`, { bid_status });
    return axiosResponse.data.result;
  },

  deleteBid: async (bid_id: string): Promise<void> => {
    await api.delete(`bids/${bid_id}`);
  },
};
